/**
 * Configuration centralisée des variables d'environnement
 * Validation au démarrage et typage TypeScript
 */

export interface EnvironmentValidationResult {
  mode: string
  missingVars: string[]
  invalidVars: string[]
  warnings: string[]
  isValid: boolean
}

/**
 * Variables d'environnement requises pour l'application
 */
const REQUIRED_ENV_VARS = [
  // URLs de base
  'VUE_APP_BASE_URL',
  'VUE_APP_BASE_URL_WEB',

  // PowerBI - Transparence
  'VUE_APP_POWERBI_TRANSPA_GROUPID',
  'VUE_APP_POWERBI_TRANSPAARCHIVESDETAILS_REPORTID',

  // PowerBI - Solvabilité (SCR)
  'VUE_APP_POWERBI_SCRARCHIVESDETAILS_REPORTID',

  // ESG et Risk sont des modules désactivés (hasEsgRights/hasRiskRights = false,
  // routes retirées du router) : leurs variables PowerBI ne sont plus requises.
  // Voir docs/MODULES-OBSOLETES.md. Ne pas réintroduire sans réactivation explicite.
] as const

/**
 * Récupère une variable d'environnement (compatible Vite et Vue CLI)
 */
function getEnvVar(key: string): string | undefined {
  // Vite utilise import.meta.env, Vue CLI utilise process.env
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] as string | undefined
  }
  return process.env[key]
}

/**
 * Valide le format d'une URL
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

function getRuntimeMode(): string {
  return import.meta?.env?.MODE || process.env.NODE_ENV || 'development'
}

function formatValidationMessage(result: EnvironmentValidationResult): string {
  const sections: string[] = []

  if (result.missingVars.length > 0) {
    sections.push(
      `Variables d'environnement manquantes:\n${result.missingVars.map(v => `  - ${v}`).join('\n')}`
    )
  }

  if (result.invalidVars.length > 0) {
    sections.push(
      `Variables d'environnement invalides:\n${result.invalidVars.map(v => `  - ${v}`).join('\n')}`
    )
  }

  return `${sections.join('\n\n')}\n\nVerifiez votre fichier .env.${result.mode}.local`
}

/**
 * Valide la présence de toutes les variables d'environnement requises
 */
export function validateEnvironment(options?: { strict?: boolean }): EnvironmentValidationResult {
  const mode = getRuntimeMode()
  const missingVars = REQUIRED_ENV_VARS.filter(varName => !getEnvVar(varName))
  const invalidVars: string[] = []
  const warnings: string[] = []

  const baseUrl = getEnvVar('VUE_APP_BASE_URL')
  const baseUrlWeb = getEnvVar('VUE_APP_BASE_URL_WEB')

  if (baseUrl && !isValidUrl(baseUrl)) {
    invalidVars.push(`VUE_APP_BASE_URL=${baseUrl}`)
  }

  if (baseUrlWeb && !isValidUrl(baseUrlWeb)) {
    invalidVars.push(`VUE_APP_BASE_URL_WEB=${baseUrlWeb}`)
  }

  if (!getEnvVar('VITE_SENTRY_DSN') && mode !== 'development') {
    warnings.push('VITE_SENTRY_DSN absent: les erreurs de production ne seront pas remontees dans Sentry.')
  }

  const result: EnvironmentValidationResult = {
    mode,
    missingVars,
    invalidVars,
    warnings,
    isValid: missingVars.length === 0 && invalidVars.length === 0,
  }

  if (!result.isValid) {
    const errorMessage = formatValidationMessage(result)
    console.error(errorMessage)

    if (mode === 'development') {
      displayMissingVarsWarning(missingVars)
    }

    if (options?.strict ?? mode !== 'development') {
      throw new Error(errorMessage)
    }
  }

  warnings.forEach(message => console.warn(message))

  return result
}

/**
 * Affiche un avertissement visuel pour les variables manquantes
 */
function displayMissingVarsWarning(missingVars: readonly string[]): void {
  console.warn('⚠️ ATTENTION: Certaines variables d\'environnement sont manquantes')
  console.warn('Les fonctionnalités PowerBI pourraient ne pas fonctionner correctement')
  console.warn('Variables manquantes:', missingVars)
}

/**
 * Configuration exportée
 */
export const config = {
  // URLs
  get baseUrl(): string {
    return getEnvVar('VUE_APP_BASE_URL') || ''
  },
  
  get baseUrlWeb(): string {
    return getEnvVar('VUE_APP_BASE_URL_WEB') || ''
  },
  
  // PowerBI configurations
  powerbi: {
    transparency: {
      get groupId(): string {
        return getEnvVar('VUE_APP_POWERBI_TRANSPA_GROUPID') || ''
      },
      reports: {
        get details(): string {
          return getEnvVar('VUE_APP_POWERBI_TRANSPAARCHIVESDETAILS_REPORTID') || ''
        },
        get historical(): string {
          return getEnvVar('VUE_APP_POWERBI_TRANSPAARCHIVESHISTO_REPORTID') || ''
        },
        get comparison(): string {
          return getEnvVar('VUE_APP_POWERBI_TRANSPAARCHIVESCOMPA_REPORTID') || ''
        },
      }
    },
    solvency: {
      get groupId(): string {
        return getEnvVar('VUE_APP_POWERBI_SCR_GROUPID') || ''
      },
      reports: {
        get details(): string {
          return getEnvVar('VUE_APP_POWERBI_SCRARCHIVESDETAILS_REPORTID') || ''
        },
        get historical(): string {
          return getEnvVar('VUE_APP_POWERBI_SCRARCHIVESHISTO_REPORTID') || ''
        },
        get comparison(): string {
          return getEnvVar('VUE_APP_POWERBI_SCRARCHIVESCOMPA_REPORTID') || ''
        },
      }
    },
    esg: {
      get groupId(): string {
        return getEnvVar('VUE_APP_POWERBI_ESG_GROUPID') || ''
      },
      reports: {
        get details(): string {
          return getEnvVar('VUE_APP_POWERBI_ESGARCHIVESDETAILS_REPORTID') || ''
        },
        get historical(): string {
          return getEnvVar('VUE_APP_POWERBI_ESGARCHIVESHISTO_REPORTID') || ''
        },
        get comparison(): string {
          return getEnvVar('VUE_APP_POWERBI_ESGARCHIVESCOMPA_REPORTID') || ''
        },
        get temperature(): string {
          return getEnvVar('VUE_APP_POWERBI_ESGARCHIVESTEMPERATURE_REPORTID') || ''
        },
      }
    },
    risk: {
      get groupId(): string {
        return getEnvVar('VUE_APP_POWERBI_RISK_GROUPID') || ''
      },
      reports: {
        get details(): string {
          return getEnvVar('VUE_APP_POWERBI_RISKLIQUIDITYARCHIVESDETAILS_REPORTID') || ''
        },
      }
    },
  }
}

export default config
